<template>
  <!-- Public API -->
  <div v-if="!isInitializing">
    <p-divider></p-divider>
    <form class="p-dynamic-form__divider" @submit.prevent="handleSubmit">
      <div v-for="field in sortedFields" :key="field.id">
        <PFormGroup :errors="errors" :errors-name-list="[field.id]" class="mb-24">
          <PPageSubtitle v-if="field.type === 'title'" :title="field.text.title[language]" />

          <PTextInput
            v-else-if="['text', 'number'].includes(field.type)"
            :id="field.id"
            v-model="formData[field.id]"
            :name="field.id"
            :type="field.type"
            :placeholder="field.text.title[language]"
            :error="!!errors[field.id]"
            @change="validateField(field)"
          />

          <PTextInput
            v-else-if="field.type === 'phone'"
            :id="field.id"
            :name="field.id"
            :placeholder="field.text.title[language]"
            :model-value="formatPhoneDisplay(formData[field.id])"
            :error="!!errors[field.id]"
            inputmode="numeric"
            autocomplete="tel"
            :maxlength="field?.maxLength || 17"
            @update:model-value="setPhoneValue(field, $event)"
          />

          <PEmailInput
            v-else-if="field.type === 'email'"
            :id="field.id"
            v-model="formData[field.id]"
            :name="field.id"
            :placeholder="field.text.title[language]"
            :error="!!errors[field.id]"
            @change="validateField(field)"
          />

          <PTextarea
            v-else-if="field.type === 'textarea'"
            :id="field.id"
            v-model="formData[field.id]"
            :name="field.id"
            :label="field.text.title[language]"
            :rows="field.rows || 3"
            :max-length="field.maxLength"
            :error="!!errors[field.id]"
            @change="validateField(field)"
          />

          <div v-else-if="field.type === 'checkbox'">
            <div
              v-for="(option, index) in field.text.options[language] || []"
              :key="`${field.id}-${index}`"
            >
              <PCheckBox
                :id="`${field.id}-${index}`"
                v-model="formData[field.id]"
                :name="field.id"
                :value="option"
                class="mb-16"
                @change="validateField(field)"
              >
                <template #append>
                  {{ option }}
                </template>
              </PCheckBox>
            </div>
          </div>

          <div v-else-if="field.type === 'radio'">
            <div v-for="(option, index) in field.text.options[language] || []" :key="index">
              <PRadio
                :id="field.id"
                v-model="formData[field.id]"
                :name="field.id"
                :label="option"
                :radio-value="option"
                class="mb-8"
                @update:model-value="setFieldValue(field, $event)"
              />
            </div>
          </div>

          <template v-else-if="field.type === 'select'">
            <PMultiSelect
              v-if="field.multiple"
              :id="field.id"
              v-model="formData[field.id]"
              :name="field.id"
              :label="field.text.title[language]"
              :searchable="field.searchable"
              :error="!!errors[field.id]"
              :options="mapOptions(getSelectOptions(field))"
              @on-change="setFieldValue(field, $event)"
            />

            <PSelect
              v-else
              v-model="formData[field.id]"
              :name="field.id"
              :label="field.text.title[language]"
              :searchable="field.searchable"
              :error="!!errors[field.id]"
              :items="mapItems(getSelectOptions(field))"
              :readonly="true"
              @update:model-value="setFieldValue(field, $event)"
            />
          </template>

          <PUpload
            v-else-if="field.type === 'file'"
            :id="field.id"
            v-model="formData[field.id]"
            :name="field.id"
            :label="field.text.title[language]"
            :tooltip="field.text.tooltip?.[language]"
            :accept="field.accept || '*'"
            :assistive-text="field.text.subtitle[language]"
            :max-size="field.maxSize"
            :error="!!errors[field.id]"
            @update:model-value="setFieldValue(field, $event)"
          />

          <PCalendar
            v-else-if="field.type === 'date'"
            v-model="formData[field.id]"
            :name="field.id"
            :date="formData[field.id]"
            :title="field.text.title[language]"
            :min-date="field.minDate"
            :max-date="field.maxDate"
            :error="!!errors[field.id]"
            @day-click="setDateValue(field, $event)"
          />
        </PFormGroup>
      </div>

      <PButton type="submit">{{ $t('form.createTicket') }}</PButton>
    </form>
  </div>

  <div v-else class="p-dynamic-form__loading">
    <p-divider></p-divider>
  </div>
</template>

<script setup lang="ts">
import { ref, watchEffect, reactive } from 'vue'
import { useI18n } from 'vue-i18n'

import { useUserStore } from '@/store/user'
import { formMaskOptions } from '@/constants/formMaskOptions'

interface Field {
  id: string
  name: string
  type: string
  order: number
  required: boolean
  notDisplay: boolean
  maxLength?: number
  minLength?: number
  rows?: number
  autoResize?: boolean
  multiple?: boolean
  searchable?: boolean
  maxSize?: number
  maxDate?: string
  minDate?: string
  includeTime?: boolean
  format?: string
  mask?: string
  accept?: string
  text: {
    subtitle: Record<string, string>
    title: Record<string, string>
    tooltip: Record<string, string>
    options: Record<string, string[]>
  }
}

const props = defineProps<{
  schema: {
    name: string
    is_login_required: boolean
    fields: Field[]
  }
}>()

const emit = defineEmits<{
  submit: [data: Record<string, any>]
}>()

const language = useI18n().locale.value.split('-')[0]
const { isInitializing } = useUserStore()
const HIDDEN_FIELD_NAMES = ['Ad', 'Soyad', 'Kayıtlı e-posta adresi']

const sortedFields = ref<Field[]>([])
const formData = ref<Record<string, any>>({})
const errors = reactive<Record<string, string>>({})

watchEffect(() => {
  if (props.schema?.fields) {
    sortedFields.value = [...props.schema.fields]
      .filter((f) => !HIDDEN_FIELD_NAMES.includes(f.name))
      .sort((a, b) => a.order - b.order)
    sortedFields.value.forEach((field) => {
      if (field.type === 'checkbox') {
        formData.value[field.id] = []
      } else if (field.type === 'file') {
        formData.value[field.id] = null
      } else {
        formData.value[field.id] = ''
      }
    })
  }
})

const handleSubmit = () => {
  if (validateForm()) {
    emit('submit', transformDataByName())
  }
}

const transformDataByName = (): Record<string, any> => {
  const result: Record<string, any> = {}
  sortedFields.value.forEach((field) => {
    result[field.name] = formData.value[field.id]
  })
  return result
}

const validateForm = () => {
  let isValid = true
  sortedFields.value.forEach((field) => {
    if (!validateField(field)) {
      isValid = false
    }
  })
  return isValid
}

const normalizePhoneDigits = (s = '') => {
  let d = s.replace(/\D/g, '')
  if (d.startsWith('0090')) d = '0' + d.slice(4)
  else if (d.startsWith('90')) d = '0' + d.slice(2)
  if (d && d[0] !== '0') d = '0' + d
  return d.slice(0, 11)
}

const formatPhoneDisplay = (raw = '') => {
  const d = normalizePhoneDigits(raw)
  if (!d) return ''

  const rest = d.startsWith('0') ? d.slice(1) : d
  let out = '0'

  if (rest.length) {
    out += ' (' + rest.slice(0, Math.min(3, rest.length))
    if (rest.length >= 3) out += ')'
  }
  if (rest.length > 3) out += ' ' + rest.slice(3, Math.min(6, rest.length))
  if (rest.length > 6) out += ' ' + rest.slice(6, Math.min(8, rest.length))
  if (rest.length > 8) out += ' ' + rest.slice(8, Math.min(10, rest.length))
  return out
}

const validateField = (field: Field) => {
  const validationMessages: any = {
    general: {
      required: {
        tr: 'Bu alan boş bırakılamaz.',
        en: 'This field cannot be empty.',
      },
      minLength: {
        tr: `En az ${field.minLength} karakter giriniz.`,
        en: `Please enter at least ${field.minLength} characters.`,
      },
      maxLength: {
        tr: `En fazla ${field.maxLength} karakter girilebilir.`,
        en: `Maximum ${field.maxLength} characters allowed.`,
      },
      invalidFormat: {
        tr: 'Geçerli bir format giriniz.',
        en: 'Please enter a valid format.',
      },
    },
    email: {
      required: {
        tr: 'E-posta boş bırakılamaz.',
        en: 'Email cannot be empty.',
      },
      invalidFormat: {
        tr: 'Geçerli bir e-posta adresi giriniz.',
        en: 'Please enter a valid email address.',
      },
    },
    phone: {
      required: {
        tr: 'Telefon numarası boş bırakılamaz.',
        en: 'Phone number cannot be empty.',
      },
      invalidFormat: {
        tr: 'Geçerli bir telefon numarası giriniz.',
        en: 'Please enter a valid phone number.',
      },
    },
    select: {
      required: {
        tr: 'Bir seçim yapmalısınız.',
        en: 'You must make a selection.',
      },
    },
    checkbox: {
      required: {
        tr: 'En az bir kutucuğu işaretlemelisiniz.',
        en: 'You must check at least one box.',
      },
    },
    file: {
      required: {
        tr: 'Bir dosya yüklemelisiniz.',
        en: 'You must upload a file.',
      },
      maxSize: {
        tr: `Dosya boyutu ${field.maxSize} MB'den büyük olamaz.`,
        en: `File size cannot exceed ${field.maxSize} MB.`,
      },
      invalidType: {
        tr: 'Geçersiz dosya türü.',
        en: 'Invalid file type.',
      },
    },
    date: {
      required: {
        tr: 'Bir tarih seçmelisiniz.',
        en: 'You must select a date.',
      },
      invalidFormat: {
        tr: `Geçerli bir tarih formatı kullanınız (${field.format}).`,
        en: `Use a valid date format (${field.format}).`,
      },
      minDate: {
        tr: `Seçilen tarih en erken ${field.minDate} olabilir.`,
        en: `Selected date must be on or after ${field.minDate}.`,
      },
      maxDate: {
        tr: `Seçilen tarih en geç ${field.maxDate} olabilir.`,
        en: `Selected date must be on or before ${field.maxDate}.`,
      },
    },
    radio: {
      required: {
        tr: 'Bir seçenek işaretlemelisiniz.',
        en: 'You must select an option.',
      },
    },
  }

  const value = formData.value[field.id]

  if (field.required) {
    if (field.type === 'checkbox') {
      if (!value || value.length === 0) {
        errors[field.id] = validationMessages.checkbox.required[language]
        return false
      }
    } else if (field.type === 'radio') {
      if (!value) {
        errors[field.id] = validationMessages.radio.required[language]
        return false
      }
    } else if (!value || value.toString().trim() === '') {
      if (field.type === 'email') {
        errors[field.id] = validationMessages.email.required[language]
      } else if (field.type === 'phone') {
        errors[field.id] = validationMessages.phone.required[language]
      } else if (field.type === 'select') {
        errors[field.id] = validationMessages.select.required[language]
      } else if (field.type === 'file') {
        errors[field.id] = validationMessages.file.required[language]
      } else if (field.type === 'date') {
        errors[field.id] = validationMessages.date.required[language]
      } else {
        errors[field.id] = validationMessages.general.required[language]
      }
      return false
    }
  }

  if (field.minLength && value.length < field.minLength) {
    errors[field.id] = validationMessages.general.minLength[language]
    return false
  }

  if (field.maxLength && value.length > field.maxLength) {
    errors[field.id] = validationMessages.general.maxLength[language]
    return false
  }

  if (field.type === 'email') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(value)) {
      errors[field.id] = validationMessages.email.invalidFormat[language]
      return false
    }
  }

  if (field.type === 'phone') {
    const digits = value.replace(/\D/g, '')
    const normalized = digits ? (digits[0] === '0' ? digits : '0' + digits) : ''
    if (normalized && !/^0\d{10}$/.test(normalized)) {
      errors[field.id] = validationMessages.phone.invalidFormat[language]
      return false
    }
  }

  if (field.type === 'date' && formData.value[field.id]) {
    const val = new Date(formData.value[field.id])
    const minDate = field.minDate ? new Date(field.minDate) : null
    const maxDate = field.maxDate ? new Date(field.maxDate) : null

    if (minDate && val < minDate) {
      errors[field.id] =
        {
          tr: 'Seçilen tarih çok erken.',
          en: 'Selected date is too early.',
        }[language] || ''
      return false
    }

    if (maxDate && val > maxDate) {
      errors[field.id] =
        {
          tr: 'Seçilen tarih çok geç.',
          en: 'Selected date is too late.',
        }[language] || ''
      return false
    }
  }

  errors[field.id] = ''
  return true
}

const setFieldValue = (field: Field, val: any) => {
  formData.value[field.id] = val
  validateField(field)
}

const setPhoneValue = (field: Field, val: string) => {
  formData.value[field.id] = normalizePhoneDigits(val)
  validateField(field)
}

const setDateValue = (field: Field, payload: any) => {
  formData.value[field.id] = payload?.date
}

const getSelectOptions = (field: Field): string[] => {
  if (field.mask && formMaskOptions[field.mask]) {
    return formMaskOptions[field.mask]
  }
  return field.text.options[language] || []
}

const mapOptions = (arr: string[]): Array<{ value: string; title: string }> => {
  return (arr || []).map((opt: string) => ({ value: opt, title: opt }))
}

const mapItems = (arr: string[]): Array<{ value: string; label: string }> => {
  return (arr || []).map((opt: string) => ({ value: opt, label: opt }))
}
</script>
